import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, Plus, Trash2, UserCircle, Loader2, Crown, User } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: "chairman", label: "Chairman" },
  { value: "secretary", label: "Secretary" },
  { value: "treasurer", label: "Treasurer" },
  { value: "member", label: "Committee Member" }
];

export const Members = () => {
  const [members, setMembers] = useState([]);
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    flat_number: "",
    flat_area: "",
    is_committee: false,
    role: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const socRes = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await socRes.json();
      
      if (societies.length > 0) {
        setSociety(societies[0]);
        const membersRes = await fetch(`${API}/societies/${societies[0].society_id}/members`, { 
          credentials: "include" 
        });
        if (membersRes.ok) {
          setMembers(await membersRes.json());
        }
      }
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!society) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        flat_number: formData.flat_number,
        flat_area: formData.flat_area ? parseFloat(formData.flat_area) : 0,
        is_committee: formData.is_committee,
        role: formData.is_committee ? formData.role : null
      };

      const res = await fetch(`${API}/societies/${society.society_id}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });

      if (res.ok) {
        const newMember = await res.json();
        setMembers([...members, newMember]);
        toast.success("Member added successfully");
        setDialogOpen(false);
        setFormData({
          name: "",
          email: "",
          flat_number: "",
          flat_area: "",
          is_committee: false,
          role: ""
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to add member");
      }
    } catch (error) {
      toast.error("Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;

    try {
      const res = await fetch(`${API}/societies/${society.society_id}/members/${memberId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (res.ok) {
        setMembers(members.filter(m => m.member_id !== memberId));
        toast.success("Member removed");
      } else {
        toast.error("Failed to remove member");
      }
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const getRoleLabel = (role) => {
    return ROLES.find(r => r.value === role)?.label || role;
  };

  const totalArea = members.reduce((sum, m) => sum + (m.flat_area || 0), 0);
  const committeeMembersCount = members.filter(m => m.is_committee).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <NeedsSetup message="Set up your society to invite and manage members." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Member Management
            </h1>
            <p className="text-slate-600">Manage society members for voting and consent</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-member-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Add New Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Rajesh Kumar"
                    required
                    data-testid="member-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g., rajesh@example.com"
                    required
                    data-testid="member-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="flat_number">Flat Number *</Label>
                    <Input
                      id="flat_number"
                      name="flat_number"
                      value={formData.flat_number}
                      onChange={handleChange}
                      placeholder="e.g., A-101"
                      required
                      data-testid="member-flat-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="flat_area">Flat Area (sq.ft)</Label>
                    <Input
                      id="flat_area"
                      name="flat_area"
                      type="number"
                      value={formData.flat_area}
                      onChange={handleChange}
                      placeholder="e.g., 850"
                      data-testid="member-area-input"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_committee"
                    checked={formData.is_committee}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_committee: checked })}
                    data-testid="committee-checkbox"
                  />
                  <Label htmlFor="is_committee" className="cursor-pointer">
                    Committee Member
                  </Label>
                </div>
                {formData.is_committee && (
                  <div>
                    <Label htmlFor="role">Committee Role</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={(v) => setFormData({ ...formData, role: v })}
                    >
                      <SelectTrigger data-testid="role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  disabled={saving}
                  data-testid="submit-member-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Member
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {members.length}
                </p>
                <p className="text-sm text-slate-500">Total Members</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {committeeMembersCount}
                </p>
                <p className="text-sm text-slate-500">Committee Members</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {totalArea.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500">Total Area (sq.ft)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Members Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Members</CardTitle>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No members added yet</p>
                <p className="text-sm text-slate-400">Add society members to enable voting</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Flat</TableHead>
                      <TableHead>Area (sq.ft)</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.member_id} data-testid={`member-row-${member.member_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {member.is_committee ? (
                              <Crown className="w-4 h-4 text-amber-500" />
                            ) : (
                              <User className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="font-medium">{member.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{member.email}</TableCell>
                        <TableCell>{member.flat_number}</TableCell>
                        <TableCell>{member.flat_area || '-'}</TableCell>
                        <TableCell>
                          {member.is_committee ? (
                            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                              {getRoleLabel(member.role)}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">Member</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(member.member_id)}
                            data-testid={`delete-member-${member.member_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
